import React, { useEffect, useReducer, useRef, useState } from "react";
import { Box, Column, Columns, Flex, Text } from "@dna/react";
import { CautionBadge, ExpiringBadge, GreyBadge, SuccessBadge } from "../common/status-badge/StatusBadge";
import { DateDisplay } from "../common/date-display/DateDisplay";
import { CoronaVirusStatus } from "../../services/medicationmanagement/patientApi/models/PatientClinicalDetail";
import { PatientAllergy } from "../../services/medicationmanagement/patientApi/models/PatientAllergy";
import { ToolTip } from "../common/tool-tip/ToolTip";
import { ResidentImage } from "../common/resident-image/ResidentImage";
import { IndigenousStatusEnum } from "../common/enums/IndigenousStatusEnum";
import { patientService } from "../../services/medicationmanagement/patientApi/PatientApi";
import { ResidentDetailsDialog } from "../common/popups/consent-dialog/ResidentDetailsDialog";
import { ClosingTheGapStatusEnum } from "../common/enums/ClosingTheGapStatus";
import { ChartExpiryBadge } from "../resident-medications/ChartExpiryBadge";
import { DateFormats } from "../common/enums/DateFormat";
import { StatusDisplay } from "../resident-list/resident-table/StatusDisplay";
import { UserType } from "../../services/medicationmanagement/medsUser/MedsUserApi";
import StatusBadgeReducer from "../common/status-badge/StatusBadgeReducer";
import { ResidentAllergyUpdate } from "../resident-list/ResidentAllergyUpdate";
import { Patient } from "../../services/medicationmanagement/patientApi/models/Patient";
import { allergyExclusionDisplayText } from "./ResidentAllergyUtils";
import { PatientMedicareDetails } from "../../services/medicationmanagement/patientApi/models/PatientMedicareDetails";
import { PatientAllergyInfo } from "../../services/medicationmanagement/patientApi/models/PatientAllergyInfo";
import { RenderIf } from "../common/render-if/RenderIf";
import { Button, Grid, Box as MBox } from "@material-ui/core";
import styles from "./ResidentHeader.module.scss";
import { DvaDetail } from "../../services/medicationmanagement/patientApi/models/DvaDetail";
import {IndividualHealthcareIdentifier} from "../../services/healthcareidentifier/models/IndividualHealthcareIdentifier"
import { QrModal } from "../pharmacy-medications/scid-qr-code/QrModal";
import { getApiConfig } from "../../config/api.config";
import { isNullOrUndefinedOrEmptyString } from "../common/utils/util";
import { ResidentChartReview } from "./ResidentChartReview";
import { ContactSection, PanelSection } from "../common/popups/ResidentDetailsDialogSection";
import {ReactComponent as FacilityIcon} from "../../images/facility-fill.svg";
import cx from "classnames";
import { DnarIcon } from "../common/dnar-icon/DnarIcon";

let iconpack = require("@dna/icons");

/*** Consent dialog  */
let consentHeaderTitle: string = "Resident details";
let consentTitle: string = "Payment arrangements";
let consentSubTitle: string = "Closing the Gap";
let consentText: string =
  "Closing the Gap PBS co-payments apply only to eligible Aboriginal and Torres Strait Islander residents. If the resident is eligible and consents, prescriptions are annotated with 'CTG' and can be dispensed with co-payment relief.";

  /*** Consent dialog  */


function CovidStatus(props: {
  status: CoronaVirusStatus | null | undefined;
  comments: string | null | undefined;
}) {
  switch (props.status) {
    case CoronaVirusStatus.Suspected:
      return (
        <ToolTip title={props.comments}>
          <CautionBadge>Suspected COVID-19</CautionBadge>
        </ToolTip>
      );

    case CoronaVirusStatus.Confirmed:
      return (
        <ToolTip title={props.comments}>
          <CautionBadge>Confirmed COVID-19</CautionBadge>
        </ToolTip>
      );

    case CoronaVirusStatus.None:
      return null;
    default:
      return null;
  }
}

function formatAllergies(allergy?: PatientAllergy) {
  if (
    allergy === undefined ||
    allergy === null ||
    (allergy.disclaimer == null &&
      allergy.note == null &&
      (allergy.allergies == undefined || allergy.allergies?.length == 0))
  ) {
    return "-";
  } else {
    return allergy.allergies?.length == 0
      ? ""
      : allergy.allergies
          ?.map((a) => `${a.allergen} (${a.reaction})`)
          .join(", ") + ". ";
  }
}

function displayCTG(indigenousStatus: string) {
  switch (indigenousStatus) {
    case IndigenousStatusEnum.AboriginalButNotTorresStraitIslanderOrigin:
      return true;
    case IndigenousStatusEnum.TorresStraitIslanderButNotAboriginalOrigin:
      return true;
    case IndigenousStatusEnum.BothAboriginalAndTorresStraitIslanderOrigin:
      return true;
    default:
      return false;
  }
}

function CTGBadge(props: {
  indigenousStatusCode: string;
  closingTheGapResponse: any;
  onClick: any;
  usertype: UserType;
}) {
  if (!displayCTG(props.indigenousStatusCode)) {
    return null;
  }
  switch (props.closingTheGapResponse) {
    case ClosingTheGapStatusEnum.Yes:
      return (
        <MBox onClick={props.onClick}
          style={{
            cursor:props.usertype === UserType.Doctor ? "pointer" : "default"
          }}>
          <SuccessBadge
          >
            CTG
          </SuccessBadge>
        </MBox>
      );
    case ClosingTheGapStatusEnum.No:
      return (
        <MBox onClick={props.onClick}
        style={{
          cursor:props.usertype === UserType.Doctor ? "pointer" : "default"
        }}>
          <GreyBadge>
            No CTG
          </GreyBadge>
        </MBox>
      );
    default:
      return (
        <MBox onClick={props.onClick}
          style={{
            cursor:props.usertype === UserType.Doctor ? "pointer" : "default"
          }}>
          <ExpiringBadge>
            CTG not answered
          </ExpiringBadge>
        </MBox>
        
      );
  }
}

function formatDiet(diet: string) {
  return diet
    ?.split("\n")
    .map((x) => x.trim().replace(/\.$/gm, "").trim())
    .join(". ");
}

const DvaDetails = (props: DvaDetail) => {
  let dvaNumber = props?.dvaNumber;
  let dvaExpiry = props?.dvaExpiry;
  return (
    <>
      {dvaNumber}
      <RenderIf if={!!dvaExpiry}>
        <span>
          {" "}
          (expiry{" "}
          <DateDisplay
            date={dvaExpiry as any}
            format={DateFormats.MMYYYY}
            isTextBold={false}
          />
          )
        </span>
      </RenderIf>
    </>
  );
};

const MedicareNumber = (props: PatientMedicareDetails | null | undefined) => {
  let cardNumber = props?.cardNumber;
  let expiresOn = props?.expiresOn;
  let formattedNumber = cardNumber;
  if (cardNumber && cardNumber.length >= 10) {
    formattedNumber = `${cardNumber.substr(0, 4)} ${cardNumber.substr(
      4,
      5
    )} ${cardNumber.substr(9)}`;
  }
  if (formattedNumber && props?.reference) {
    formattedNumber = `${formattedNumber} - ${props.reference}`;
  }
  return (
    <>
      {formattedNumber}
      <RenderIf if={!!expiresOn}>
        <span>
          {" "}
          (expiry{" "}
          <DateDisplay
            date={expiresOn as any}
            format={DateFormats.MMYYYY}
            isTextBold={false}
          />
          )
        </span>
      </RenderIf>
    </>
  );
};

const StaticControlBox = (props: { children: JSX.Element }) => {
  return (
    <Box
      css={{
        marginStart: "200",
      }}
    >
      {props.children}
    </Box>
  );
};

const DisplayPrescriptionIdentifiter = (props : { patientInfo: Patient }) => {
    const { chart, givenName, familyName } = props.patientInfo;
    const flag = getApiConfig().featureFlags?.qrcode;
    return (
        <Columns
                css={{
                gap: "small",
                }}
        >
            <Column width="content">
                <Text
                    level="small"
                    css={{
                      color: "muted",
                    }}
                >
                Chart script no.
                </Text>
            </Column>
            <StaticControlBox>
                <QrModal 
                  scriptNumber={chart?.prescriptionIdentifier}
                  chartUpdated={chart?.updatedDate}
                  chartExpiry={chart?.expiryDate}
                  patientName={`${givenName} ${familyName}`}
                  flag={flag}
                />
            </StaticControlBox>
        </Columns>
    );
  };

export const ResidentHeader = (props: {
  patientInfo: Patient;
  allergyInfo: PatientAllergyInfo;
  userType: UserType;
  ihi: IndividualHealthcareIdentifier;
  loadChartContactDetails?: () => void;
}) => {
  let { patientInfo, allergyInfo } = props;
  const [openResidentDetailsDialog, setOpenResidentDetailsDialog] = React.useState(false);
  const [allergyactionedconfirmed, setallergyactionedConfirmed] =
    React.useState(false);
  const [chartReviewConfiremd, setChartReviewConfiremd] =
    React.useState(false);
  const [panelSection, setPanelSection] = useState(PanelSection.Consent);
  const [contactSection, setContactSection] =  useState(ContactSection.Facility);
  const [state, dispatch] = useReducer(StatusBadgeReducer, { status: false });
  const [chartReviewState, chartReviewDispatch] = useReducer(StatusBadgeReducer, { status: false });
  const [showConsent, setShowConsent] = useState(false);

  const handleonAllergyActionConfirmed = (value: boolean) => {
    setallergyactionedConfirmed(value);
    if(value){
      if(props.loadChartContactDetails !== undefined){
          props.loadChartContactDetails();
      }
    }
  };

  const handleOnChartReviewConfirmed = (value: boolean) => {
    setChartReviewConfiremd(value);
    if(value){
      if(props.loadChartContactDetails !== undefined){
          props.loadChartContactDetails();
      }
    }
  };

  const savePatientCTGResponse = async (ctgResponse: string) => {
    let updateSuccess = await patientService.saveCTGResponse(
      patientInfo.id,
      ctgResponse,
      patientInfo.authoringSystem,
      patientInfo.authoringSystemForeignKey
    );
    if (updateSuccess) {
      patientInfo.closingTheGap.code = ctgResponse;
      setOpenResidentDetailsDialog(false);
    }
    return updateSuccess;
  };

  let newDisclaimer = allergyExclusionDisplayText(
    allergyInfo?.allergyHistory?.newDisclaimer
  );
  let disclaimer = allergyExclusionDisplayText(
    allergyInfo?.allergyHistory?.disclaimer
  );

  const contactModal = useRef<any>(null);

  const openContactDialog = (scrollToSection: ContactSection) => {

    setPanelSection(PanelSection.Contacts);
    setContactSection(scrollToSection);
    //Open Dialog
    setOpenResidentDetailsDialog(true); 
  };

  const openConsentDialog = () => {
    if(props.userType !== UserType.Doctor){
      return;
    }
    setPanelSection(PanelSection.Consent);
    setOpenResidentDetailsDialog(true); 
  };

  useEffect(() => {
    // display Consent section in details modal only if indigenous status is set and logged in user is a prescriber
    setShowConsent(displayCTG(patientInfo?.indigenousStatusCode) && (props.userType === UserType.Doctor));
  },[]);

  const handleClose = () => {
    setOpenResidentDetailsDialog(false);
    setTimeout(() => {
      setPanelSection(PanelSection.Consent);
      setContactSection(ContactSection.Facility);
    }, 500);
  };

  return (
    <Grid container spacing={0} className={styles.headerRoot}>
      <Grid item xs={8}>
        <Grid container spacing={1} className={styles.residentInfoContainer}>
          <Grid item>
            <ResidentImage
              authoringSystem={patientInfo?.authoringSystem}
              authoringSystemForeignKey={patientInfo?.authoringSystemForeignKey}
              imageWidth={80}
              imageHeight={80}
            />
          </Grid>

          <Grid item className={styles.fullWidth}>
            <Flex
              css={{
                gap: "0"
              }}
              UNSAFE_className={styles.residentNameContainer}
            >
              {patientInfo?.residentWithSimilarNamePresent ? (
                <Text
                  level="small"
                  css={{
                    color: "critical",
                  }}
                >
                  There is a resident with a similar or same name
                </Text>
              ) : null}
            </Flex>
            <Columns css={{ gap: "small" }} UNSAFE_className={styles.formFieldContainer}>
              <Column
                width="content"
                css={{
                  isHidden:
                    patientInfo?.clinicalDetail?.coronaVirusStatus == null ||
                    patientInfo?.clinicalDetail?.coronaVirusStatus ==
                      CoronaVirusStatus.None,
                }}
              >
                <CovidStatus
                  status={patientInfo?.clinicalDetail?.coronaVirusStatus}
                  comments={patientInfo?.clinicalDetail?.healthComment}
                />
              </Column>
              <Column
                width="content"
                css={{
                  isHidden: !displayCTG(patientInfo?.indigenousStatusCode),
                }}
              >
              <CTGBadge
                  indigenousStatusCode={patientInfo?.indigenousStatusCode}
                  closingTheGapResponse={patientInfo?.closingTheGap.code}
                  onClick={() => openConsentDialog()}
                  usertype={props.userType}
                />
              </Column>
              <Column width="content">
                <ChartExpiryBadge
                  isReviewDue={patientInfo?.chart?.isReviewDue}
                  isReviewSoon={patientInfo?.chart?.isReviewSoon}
                  expiryDate={patientInfo?.chart?.expiryDate}
                />
              </Column>
              {props.userType === UserType.Pharmacist &&
              (patientInfo.chart?.hasChartChanged
                || (patientInfo.chart?.hasOnlyChartReviewChanges && !chartReviewConfiremd )
              )
               && (
                <Column width="content">
                  <StatusDisplay
                    statuses={{
                      isReviewSoonRequired: false,
                      isReviewDue: false,
                      isCourseExpiringSoon: false,
                      isCourseExpired: false,
                      hasChartChanged: patientInfo.chart?.hasChartChanged,
                      isNewAdmission: false,
                      hasOpenPhoneOrder: false,
                      hasOnlyChartReviewChanges: patientInfo.chart?.hasOnlyChartReviewChanges
                    }}
                    chartReviewConfiremd = {chartReviewConfiremd}
                    userType={props.userType}
                  />
                </Column>
              )}

               {!allergyactionedconfirmed &&
                props.userType === UserType.Pharmacist &&
                (allergyInfo.allergyHistory?.newNote ||
                  allergyInfo.allergyHistory?.newAllergyHistoryDetails ||
                  allergyInfo.allergyHistory?.newDisclaimer) && 
                  (<Column width="content">
                    <ResidentAllergyUpdate
                      dispatch={dispatch}
                      state={state.status}
                      disclaimer={disclaimer}
                      newdisclaimer={newDisclaimer}
                      handleonAllergyActionConfirmed={
                        handleonAllergyActionConfirmed
                      }
                      patient={{
                        authoringSystem: patientInfo.authoringSystem || "",
                        authoringSystemForeignKey:
                          patientInfo.authoringSystemForeignKey || "",
                        ...allergyInfo,
                      }}
                    />
                  </Column>)}

              {!chartReviewConfiremd && props.userType === UserType.Pharmacist && !isNullOrUndefinedOrEmptyString(patientInfo.chart?.lastChartReviewDate) && !patientInfo.chart?.isChartReviewConfirmed &&
              (<Column width="content">
                <ResidentChartReview
                    dispatch={chartReviewDispatch}
                    state={chartReviewState.status}
                    handleOnChartReviewConfirmed={
                      handleOnChartReviewConfirmed
                    }
                    patient={{
                      authoringSystem: patientInfo.authoringSystem || "",
                      authoringSystemForeignKey:
                        patientInfo.authoringSystemForeignKey || "",
                      lastPharmacyMedicationOrderId: patientInfo.chart?.lastPharmacyMedicationOrderId
                    }}
                  />
                </Column>)}
            </Columns>
            <div ref={contactModal} >
              <ResidentDetailsDialog
                HeaderTitle={consentHeaderTitle}
                ConsentTitle={consentTitle}
                ConsentSubTitle={consentSubTitle}
                ConsentText={consentText}
                selectedValue={patientInfo?.closingTheGap.code}
                open={openResidentDetailsDialog}
                onSave={(ctgResponse) => savePatientCTGResponse(ctgResponse)}
                panelSection={panelSection} 
                contactSection={contactSection}
                handleClose={() => handleClose() }
                resident={patientInfo}
                showConsent={showConsent}
              />
            </div>
            <MBox className={styles.formFieldContainer}>
              <MBox className={styles.formField}>
                <span className={styles.label}>Age</span>
                <span>
                  <>
                    {patientInfo?.age} yo (
                    <DateDisplay
                      date={patientInfo?.dateOfBirth}
                      format={DateFormats.DDMMMYYYY}
                      isTextBold={false}
                    />
                    )
                  </>
                </span>
                <span className={styles.label}>|</span>
                <span className={styles.label}>Gender</span>
                <span>
                  {patientInfo?.sex?.name}
                </span>
              </MBox>
            </MBox>

            <MBox className={styles.formFieldContainer}>
              <MBox className={styles.formField}>
                <span className={styles.label}>Medicare #</span>
                <span>
                  <MedicareNumber
                  expiresOn={
                    patientInfo?.identifiers?.medicareDetails?.expiresOn
                  }
                  reference={
                    patientInfo?.identifiers?.medicareDetails?.reference
                  }
                  cardNumber={
                    patientInfo?.identifiers?.medicareDetails?.cardNumber
                  }
                />
                </span>
                <span className={styles.label}>|</span>
                <span className={styles.label}>DVA #</span>
                <span>
                  <DvaDetails {...patientInfo.dvaDetails} />
                </span>
                <span className={styles.label}>|</span>
                <span className={styles.label}>Centrelink / pension #</span>
                <span>{patientInfo?.centrelinkNumber}</span>
              </MBox>
            </MBox>


            <MBox className={styles.formFieldContainer}>
              <MBox className={styles.formField}>
                <span className={styles.label}>Diet</span>
                <span>{formatDiet(patientInfo?.clinicalDetail?.diet)}</span>
              </MBox>
            </MBox>
            <MBox className={styles.formFieldContainer}>
              <MBox className={styles.formField}>
                <span className={styles.label}>Special needs / diagnosis</span>
                <span>{patientInfo?.clinicalDetail?.specialNeeds}</span>
              </MBox>
            </MBox>
            <MBox className={styles.formFieldContainer}>
              <MBox className={styles.formField}>
                <span className={styles.label}>
                  Medication note (special instructions)
                </span>
                <span>{patientInfo?.clinicalDetail?.medicationNote}</span>
              </MBox>
            </MBox>
            <MBox > 
                <Button onClick={() => openContactDialog(ContactSection.Facility)} className={cx(styles.link, styles.zeroLeftPadding)}> 
                <FacilityIcon></FacilityIcon>
                {patientInfo?.facilityDetails?.name} 
                </Button>
                {patientInfo?.contacts?.recentActivePrescribers?.length>0 &&(
                <Button onClick={() => openContactDialog(ContactSection.Prescriber)} className={cx(styles.link, styles.lineHeight)}>
                  <DnarIcon name="gp-fill"></DnarIcon>
                  {patientInfo?.contacts.recentActivePrescribers[0].givenName} {patientInfo?.contacts?.recentActivePrescribers[0].familyName} ...
                  </Button>
                  )}
                {patientInfo?.contacts?.recentActivePharmacists?.length>0 && (
                <Button onClick={() => openContactDialog(ContactSection.Pharmacist)} className={cx(styles.link, styles.lineHeight)}> 
                  <DnarIcon name="pharmacy-fill"></DnarIcon>
                  {patientInfo?.contacts.recentActivePharmacists[0].givenName} {patientInfo?.contacts?.recentActivePharmacists[0].familyName} ...
                </Button>
                )}
               </MBox>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={4} className={styles.allergyDetails}>
        <Flex
          css={{
            gap: "small",
            paddingEnd: "small",
          }}
        >
          <Columns
            css={{
              gap: "small",
            }}
          >
            <Column width="content">
              <Text
                level="small"
                css={{
                  color: "muted",
                }}
              >
                Resident details updated
              </Text>
            </Column>
            <StaticControlBox>
              <Text level="small">
                <DateDisplay
                  date={patientInfo?.lastUpdatedOn}
                  format={DateFormats.DDMMMYYYY}
                  isTextBold={false}
                />
              </Text>
            </StaticControlBox>
          </Columns>
          {patientInfo?.chart?.prescriptionIdentifier && 
          <DisplayPrescriptionIdentifiter patientInfo = {patientInfo}></DisplayPrescriptionIdentifiter>
          }
          <Columns
            css={{
              gap: "small",
              backgroundColor: "orange-lighter",
            }}
            UNSAFE_style={{
              wordBreak: "break-word",
            }}
          >
            <Column
              width="content"
              css={{
                color: "critical",
                paddingTop: "small",
                paddingStart: "small",
                borderStartColor: "orange-mid",
                borderBottomStartRadius: "small",
                borderStartWidth: "thicker",
              }}
            >
              <iconpack.IconCaution level="medium" />
            </Column>

                
            <Column
              css={{
                minHeight: 70,
                paddingTop: "small",
              }}
            >
              <Text
                level="small"
                css={{
                  color: "critical",
                }}
                as="strong"
              >
                Allergies (and adverse reactions)
              </Text>
              <br />
              <Text
                level="small"
                css={{
                  color: "critical",
                }}
              >
                {formatAllergies(allergyInfo?.allergy)}
              </Text>
              <Text
                level="small"
                css={{
                  color: "critical",
                }}
              >
                {allergyExclusionDisplayText(allergyInfo?.allergy?.disclaimer)}
              </Text>
              <Text
                level="small"
                css={{
                  color: "critical",
                }}
              >
                {allergyInfo?.allergy?.note}
              </Text>
            </Column>
          </Columns>
        </Flex>
      </Grid>
    </Grid>
  );
};


